// @flow
import React from 'react';
import classnames from 'classnames';
import makeStyles from '@material-ui/core/styles/makeStyles';
import type { Zone } from '../../../../../types';
import useNow from '../../../../../hooks/useNow';
import Overlay from '../../../../ui/openlayers/Overlay';
import DurationInput from '../../../../ui/DurationInput';
import EnableButton from './EnableButton';

const activeBackgroundColor = '#D0041B';
const inactiveBackgroundColor = '#FFFFFF';

const useStyles = makeStyles(theme => ({
    root: {
        width: '80px'
    },
    button: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    content: {
        padding: 4,
        position: 'relative',
        borderRadius: 8,
        borderTopRightRadius: 0,
        zIndex: 1,
        fontSize: theme.typography.caption.fontSize,
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: 1
    },
    zoneName: {
        textAlign: 'center'
    },
    zoneDuration: {
        textAlign: 'center'
    },
    active: {
        backgroundColor: activeBackgroundColor,
        color: '#eeeeee'
    },
    inactive: {
        backgroundColor: inactiveBackgroundColor,
        color: '#939393'
    },
    pointer: {
        position: 'relative',
        fill: props => props.active ? activeBackgroundColor : inactiveBackgroundColor,
        zIndex: 0,
        marginLeft: '7px',
        width: 'calc(100% - 14px)',
        height: '12px',
        verticalAlign: 'top'
    }
}));

export type Props = $ReadOnly<{|
    position: [number, number],
    zone: Zone,

    activate: (zoneId: string, duration?: number) => void,
    deactivate: (zoneId: string) => void
|}>;

function Controls({ position, zone, activate, deactivate }: Props) {
    const now = useNow();
    const active = Boolean(zone.active);
    const classes = useStyles({ active });

    const onStatusChange = (active) => {
        if(active) {
            activate(zone.id);
        }
        else {
            deactivate(zone.id);
        }
    };

    const onDurationChange = (duration) => {
        activate(zone.id, duration);
    };

    const duration = (active && zone.activeUntil) ? Math.max(0, (zone.activeUntil.seconds * 1000) - now) : null;

    return (
        <Overlay position={position} positioning="bottom-center">
            <div className={classes.root}>
                <div className={classes.button}>
                    <EnableButton
                        enabled={active}
                        activeBackgroundColor={activeBackgroundColor}
                        inactiveBackgroundColor={inactiveBackgroundColor}
                        onChange={onStatusChange}
                    />
                </div>

                <div
                    className={classnames(classes.content, {
                        [classes.active]: active,
                        [classes.inactive]: !active
                    })}
                >
                    <div className={classes.zoneName}>
                        {zone.name}
                    </div>
                    <div className={classes.zoneDuration}>
                        <DurationInput
                            duration={duration}
                            onChange={onDurationChange}
                        />
                    </div>
                </div>
                <svg className={classes.pointer} viewBox="0 0 24 24" preserveAspectRatio="none" >
                    {/*<path fill="red" d="M48 13.52L64 0L32 0L0 0L16 13.52L32 27.03L48 13.52Z"></path>*/}
                    <polygon points="0,0 24,0 12,24" />
                </svg>
            </div>
        </Overlay>
    );
}

export default Controls;